import { InputOutputSelector } from '@modules/input-output-devices/InputOutputSelector/InputOutputSelector';
import { ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/system';
import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { PopoverProps } from 'src/components/PopoverTrigger';
import Iconify from 'src/components/iconify';
import { useTranscriptionNotesState } from 'src/features/ActionsBar/Gathering/GatheringTranscription/useTranscriptionNotesState';
import { useActionBarState } from 'src/features/ActionsBar/useActionBarState';
import { useGatheringChatState } from 'src/features/GatheringSessions/gatheringChatState';
import { useTranscriptionControl } from 'src/hooks/useTranscriptionControl';
import { useLocales } from 'src/locales';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';

type VoiceSettingsProps = {
  inviteUsers: (e: MouseEvent) => void;
  isGuest?: boolean;
};

export const VoiceControlSettingsPopover = ({
  target,
  handleClose,
  inviteUsers,
  isGuest,
}: PopoverProps<VoiceSettingsProps>) => {
  const open = Boolean(target);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>();

  const { translate } = useLocales();

  const { handleToggleTranscription } = useTranscriptionControl();
  const { isTranscriptionStarted, startedByCurrentUser } =
    useTranscriptionNotesState();

  const { setSessionId, sessionId } = useGatheringChatState();
  const { openAction, setOpenAction, setIsOpen } = useActionBarState();

  const { activeSessionId } = useLivekitRTCState();

  const handleOpenSettings = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleCloseSettings = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const isChatOpened =
    openAction === 'gatheringChat' && sessionId === activeSessionId;

  const handleOpenChat = useCallback(() => {
    if (!activeSessionId) return;
    if (isChatOpened) {
      setIsOpen(false);
      handleClose();
      return;
    }
    setSessionId(activeSessionId);
    setOpenAction('gatheringChat');
    handleClose();
  }, [
    activeSessionId,
    isChatOpened,
    setIsOpen,
    setSessionId,
    setOpenAction,
    handleClose,
  ]);

  const handleOpenParticipantsList = useCallback(() => {
    setOpenAction('gathering');
    handleClose();
  }, [setOpenAction, handleClose]);

  const handleLockChannel = () => {};
  const handleUnlockChannel = () => {};

  const menuOptions = useMemo(
    () => [
      {
        label: translate('communicate.rtc_controls.copera_start_transcription'),
        icon: 'tabler:file-text-spark',
        action: () => handleToggleTranscription(),
        classNames: 'only-mobile',
        disabled: isTranscriptionStarted,
        display: (!isTranscriptionStarted || !startedByCurrentUser) && !isGuest,
      },
      {
        label: translate(
          'communicate.rtc_controls.copera_start_video_transcription',
        ),
        icon: 'tabler:file-text-spark',
        action: () => handleToggleTranscription(false),
        classNames: 'only-mobile',
        disabled: isTranscriptionStarted,
        display: (!isTranscriptionStarted || !startedByCurrentUser) && !isGuest,
      },
      {
        label: translate('communicate.rtc_controls.copera_stop_transcription'),
        icon: 'stop-transcription',
        action: () => handleToggleTranscription(),
        classNames: 'only-mobile',
        display: isTranscriptionStarted && startedByCurrentUser && !isGuest,
      },
      {
        label: translate('communicate.rtc_controls.invite_users'),
        icon: 'tabler:users-plus',
        action: (e: MouseEvent) => inviteUsers(e),
        classNames: 'only-mobile',
        display: !isGuest!,
      },
      {
        label: translate('actions_bar.gathering_participants'),
        icon: 'tabler:users',
        action: () => handleOpenParticipantsList(),
        display: isGuest!,
      },
      {
        label: translate('communicate.open_chat'),
        icon: 'tabler:message',
        action: () => handleOpenChat(),
        classNames: '',
        display: true,
      },
      {
        label: translate('communicate.rtc_controls.device_settings'),
        icon: 'ic:round-settings',
        action: (e: React.MouseEvent<HTMLElement>) => handleOpenSettings(e),
        classNames: 'only-mobile',
        display: true,
      },
      {
        label: translate('communicate.rtc_controls.lock_channel'),
        icon: 'tabler:lock',
        action: () => handleLockChannel(),
        classNames: '',
        disabled: true,
        display: !isGuest,
      },
      {
        label: translate('communicate.rtc_controls.unlock_channel'),
        icon: 'tabler:lock-open',
        action: () => handleUnlockChannel(),
        classNames: '',
        disabled: true,
        display: false,
      },
    ],
    [
      isGuest,
      isTranscriptionStarted,
      startedByCurrentUser,
      translate,
      inviteUsers,
      handleToggleTranscription,
      handleOpenSettings,
      handleOpenChat,
      handleOpenParticipantsList,
    ],
  );

  return (
    <>
      <StyledMenu
        open={open}
        anchorEl={target}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {menuOptions.map(
          (option, index) =>
            option.display && (
              <MenuItem
                key={index}
                sx={{ gap: 1 }}
                onClick={option.action}
                className={option.classNames}
                disabled={option.disabled}
              >
                <ListItemIcon>
                  <Iconify icon={option.icon} color="text.secondary" />
                </ListItemIcon>
                <Typography variant="caption">{option.label}</Typography>
              </MenuItem>
            ),
        )}
      </StyledMenu>
      <InputOutputSelector
        target={anchorEl as HTMLElement}
        handleClose={handleCloseSettings}
      />
    </>
  );
};

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiButtonBase-root': {
    marginBottom: '0 !important',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 'unset !important',
  },
  [theme.breakpoints.up('md')]: {
    '& .only-mobile': {
      display: 'none',
    },
  },
}));
